import React from 'react'
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import LegalContent from 'src/components-new/layout/LegalContent'
import PasswordFieldNew from 'src/components-new/inputs/PasswordFieldNew'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {ConsentOption} from '../patientDigitalEnrollmentValidation'

const CONSENT_OPTIONS = [
  {value: 'CONSENT', label: 'Esprimo il consenso'},
  {value: 'NOT_CONSENT', label: 'Nego il consenso'},
]

export interface ConsentsStepValue {
  consents: Record<string, string>
  consentPassword: string
}

interface ConsentsStepProps {
  informationHtml?: string
  consentHtml?: string
  consentOptions: ConsentOption[]
  value: ConsentsStepValue
  onChange: (value: ConsentsStepValue) => void
  errors: Record<string, string>
  disabled?: boolean
}

/**
 * Step 2/2 — new-style equivalent of the legacy step 1 in
 * PatientDigitalEnrollment.tsx:478-535. `informationHtml`/`consentHtml`/
 * `consentCheckboxOptions` are top-level fields of the login-configuration response for
 * this flow — unlike src/pages-new/selfRegistration, which reads the differently-shaped
 * `firstLoginConfiguration.*` for the same purpose. Raw MUI Radio/RadioGroup (not
 * `RadioGroupNew`, which has no `error` styling on the radios themselves, only on the
 * helper text) — same choice as selfRegistration's ConsentsStep for the identical
 * CONSENT/NOT_CONSENT pattern.
 */
export default function ConsentsStep({
  informationHtml,
  consentHtml,
  consentOptions,
  value,
  onChange,
  errors,
  disabled,
}: ConsentsStepProps): JSX.Element {
  const setConsent = (optionValue: string, consentValue: string) => {
    onChange({...value, consents: {...value.consents, [optionValue]: consentValue}})
  }

  return (
    <Stack spacing={3}>
      {informationHtml && <LegalContent html={informationHtml} />}
      {consentHtml && <LegalContent html={consentHtml} />}

      <Typography variant="body2">
        {'Confermo di aver letto l’Informativa in materia di protezione dei Dati Personali che precede e, pertanto:'}
      </Typography>

      <Stack spacing={2}>
        {consentOptions.map((opt) => (
          <FormControl key={opt.value} error={Boolean(errors[opt.value])} component="fieldset">
            <Typography variant="body2" sx={{color: NEUTRAL_SCALE[900], mb: 0.5, fontWeight: 500}}>
              {opt.label}
            </Typography>
            <RadioGroup
              value={value.consents[opt.value] ?? ''}
              onChange={(_, v) => setConsent(opt.value, v)}
            >
              {CONSENT_OPTIONS.map((co) => (
                <FormControlLabel
                  key={co.value}
                  value={co.value}
                  control={<Radio disabled={disabled} />}
                  label={co.label}
                />
              ))}
            </RadioGroup>
            {errors[opt.value] && <FormHelperText>{errors[opt.value]}</FormHelperText>}
          </FormControl>
        ))}
      </Stack>

      <PasswordFieldNew
        label="Reinserisci la password per confermare i consensi"
        value={value.consentPassword}
        onChange={(consentPassword) => onChange({...value, consentPassword})}
        autoComplete="new-password"
        required
        fullWidth
        disabled={disabled}
        error={Boolean(errors.consentPassword)}
        helperText={errors.consentPassword}
      />
    </Stack>
  )
}
